import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../../../config';
import { searchIcon } from '../chatIcons';
import './JpAddressInput.css';

export interface JpAddressValue {
  postalCode: string; // raw user input (may include hyphen)
  pref: string;
  city: string;
  town: string;
  line2: string; // building/room etc.
}

export interface JpAddressInputProps {
  value: JpAddressValue;
  onChange: (next: JpAddressValue) => void;
}

type LookupRow = { zip7: string; prefJa: string; cityJa: string; townJa: string };

function normalizeDigits(input: string) {
  return input.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0));
}

function normalizePostalCode(input: string) {
  const half = normalizeDigits(input);
  return half.replace(/[^\d]/g, '');
}

const JpAddressInput: React.FC<JpAddressInputProps> = ({ value, onChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<LookupRow[]>([]);
  const [candidateOpen, setCandidateOpen] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  const zip7 = useMemo(() => normalizePostalCode(value.postalCode), [value.postalCode]);
  const canLookup = zip7.length === 7 && Boolean(API_BASE_URL);

  const setField = useCallback((patch: Partial<JpAddressValue>) => onChange({ ...value, ...patch }), [onChange, value]);

  const applyCandidate = useCallback(
    (row: LookupRow) => {
      setField({
        pref: row.prefJa ?? '',
        city: row.cityJa ?? '',
        town: row.townJa ?? '',
      });
      setCandidates([row]);
      setCandidateOpen(false);
    },
    [setField]
  );

  const lookup = useCallback(async () => {
    if (!canLookup) return;
    setError(null);
    setLoading(true);
    setCandidates([]);
    setCandidateOpen(false);
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const res = await fetch(`${API_BASE_URL}/api/postal-code-data/${encodeURIComponent(zip7)}`, {
        signal: ac.signal,
      });
      if (!res.ok) throw new Error(`検索に失敗しました（${res.status}）`);
      const data = (await res.json()) as any;
      const rows: LookupRow[] = Array.isArray(data)
        ? data
            .map((r) => ({
              zip7: String(r?.zip7 ?? ''),
              prefJa: String(r?.prefJa ?? ''),
              cityJa: String(r?.cityJa ?? ''),
              townJa: String(r?.townJa ?? ''),
            }))
            .filter((r) => r.zip7 && r.prefJa)
        : [];

      if (rows.length === 0) {
        setError('該当する住所が見つかりませんでした。郵便番号を確認してください。');
        setCandidates([]);
        return;
      }
      if (rows.length === 1) {
        applyCandidate(rows[0]);
        return;
      }
      setCandidates(rows);
      setCandidateOpen(true);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      setError(e instanceof Error ? e.message : '検索に失敗しました。');
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [applyCandidate, canLookup, zip7]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  return (
    <div className="jp-addr">
      <div className="jp-addr__zip-row">
        <div className="jp-addr__zip-input-wrap">
          <img src={searchIcon} alt="" aria-hidden className="jp-addr__zip-icon" />
          <input
            className="jp-addr__zip-input"
            value={value.postalCode}
            onChange={(e) => setField({ postalCode: e.target.value })}
            placeholder="郵便番号（例：100-0001）"
            inputMode="numeric"
            autoComplete="postal-code"
          />
        </div>
        <button
          type="button"
          className="jp-addr__zip-btn"
          onClick={() => void lookup()}
          disabled={!canLookup || loading}
        >
          {loading ? '検索中…' : '検索'}
        </button>
      </div>

      {error && (
        <div className="jp-addr__error" role="alert">
          {error}
        </div>
      )}

      {candidateOpen && candidates.length > 1 && (
        <div className="jp-addr__candidates">
          <div className="jp-addr__candidates-title">候補を選択してください</div>
          <div className="jp-addr__candidate-list">
            {candidates.map((c, idx) => (
              <button
                key={`${c.zip7}-${idx}`}
                type="button"
                className="jp-addr__candidate"
                onClick={() => applyCandidate(c)}
              >
                {c.prefJa}
                {c.cityJa}
                {c.townJa}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="jp-addr__grid">
        <label className="jp-addr__field">
          <div className="jp-addr__label">都道府県</div>
          <input className="jp-addr__input" value={value.pref} onChange={(e) => setField({ pref: e.target.value })} />
        </label>
        <label className="jp-addr__field">
          <div className="jp-addr__label">市区町村</div>
          <input className="jp-addr__input" value={value.city} onChange={(e) => setField({ city: e.target.value })} />
        </label>
        <label className="jp-addr__field">
          <div className="jp-addr__label">町名・番地</div>
          <input className="jp-addr__input" value={value.town} onChange={(e) => setField({ town: e.target.value })} />
        </label>
        <label className="jp-addr__field">
          <div className="jp-addr__label">建物名・部屋番号など</div>
          <input
            className="jp-addr__input"
            value={value.line2}
            onChange={(e) => setField({ line2: e.target.value })}
            placeholder="例：○○マンション 101号室"
          />
        </label>
      </div>
    </div>
  );
};

export default JpAddressInput;

